import React, { useRef, useEffect, useState, useCallback, Component } from 'react';
import { gsap } from 'gsap';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import BottleImage from './BottleImage';
import { reveal, registerScrollTrigger, prefersReducedMotion } from '../lib/motion';
import { whatsappLinkProps, productOrderMessage } from '../lib/whatsapp';

/* ── Error Boundary ─────────────────────────────────────────────────────── */
class CollectionBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return (
      <section id="collection" style={{ background: 'var(--black)', padding: '80px 24px', textAlign: 'center' }}>
        <span className="section-eyebrow">02 — Collection</span>
        <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', color: 'var(--grey)', marginTop: '16px' }}>
          Scroll down to explore our full collection
        </p>
      </section>
    );
    return this.props.children;
  }
}

/* ── Product Card ────────────────────────────────────────────────────────── */
const ProductCard = ({ product }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card || prefersReducedMotion()) return;
    const rect = card.getBoundingClientRect();
    const rotX =  ((e.clientY - rect.top  - rect.height / 2) / rect.height) * 12;
    const rotY = -((e.clientX - rect.left - rect.width  / 2) / rect.width)  * 12;
    card.style.transform = `perspective(800px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-6px)`;
    card.style.transition = 'transform 0.05s ease';
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = 'perspective(800px) rotateX(0) rotateY(0) translateY(0)';
    cardRef.current.style.transition = 'transform 0.5s ease';
  };

  return (
    <div
      ref={cardRef}
      data-testid={`product-card-${product.id}`}
      className="product-card-wrapper"
      style={{ flexShrink: 0, width: 'clamp(216px, 28vw, 240px)', background: '#0E0D0B', padding: '28px 22px 24px', position: 'relative', borderRadius: '2px', willChange: 'transform' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={() => { if (cardRef.current) cardRef.current.style.transition = 'none'; }}
    >
      <div style={{ height: '176px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
        <BottleImage product={product} height={176} />
      </div>

      <div style={{ marginTop: '18px' }}>
        {/* Reserves two lines. Seven of the twenty brand names wrap, and
            without the reserve those cards pushed their product name, price
            and Inquire button 15px lower than the rest of the row. 3em at
            line-height 1.5 is exactly two lines. */}
        <span style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '10px', letterSpacing: '1.6px', textTransform: 'uppercase', color: 'var(--gold-dark)', display: 'block', marginBottom: '8px', lineHeight: 1.5, minHeight: '3em' }}>
          {product.inspired_by}
        </span>
        <h3 style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: '15px', letterSpacing: '2.5px', color: 'var(--cream)', marginBottom: '5px', fontWeight: 400, lineHeight: 1.3 }}>
          {product.name}
        </h3>
        <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '14px', color: 'var(--grey)', marginBottom: '14px', lineHeight: 1.5 }}>
          {product.type} · {product.size}
        </p>
        <div style={{ paddingTop: '12px', borderTop: '1px solid rgba(201,169,110,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
            <span style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: '15px', color: 'var(--gold-light)', letterSpacing: '1px' }}>
              $100 <span style={{ fontFamily: 'Tenor Sans', fontSize: '10px', color: 'var(--grey)' }}>CAD</span>
            </span>
            <a
              {...whatsappLinkProps(productOrderMessage(product))}
              className="btn-gold"
              data-testid={`inquire-btn-${product.id}`}
              aria-label={`Order ${product.name} via WhatsApp`}
              style={{ padding: '8px 14px', fontSize: '10px', letterSpacing: '2px' }}
            >
              Order
            </a>
          </div>
          <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '13px', color: 'var(--grey-dim)' }}>
            100mL available upon request.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Mobile carousel ─────────────────────────────────────────────────────── */
const CARD_W = 240;
const CARD_GAP = 16;
const TRACK_PAD = 24;

const MobileCarousel = () => {
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [currentIdx, setCurrentIdx] = useState(0);

  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setProgress(maxScroll > 0 ? el.scrollLeft / maxScroll : 0);
    // scroll-snap aligns each card's leading edge to the padded content box, so
    // the first card sits at scrollLeft 0 with no padding offset to subtract.
    const idx = Math.round(el.scrollLeft / (CARD_W + CARD_GAP));
    setCurrentIdx(Math.min(Math.max(idx, 0), PRODUCTS.length - 1));
  }, []);

  const scrollTo = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({
      left: (CARD_W + CARD_GAP) * dir,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  return (
    <div style={{ paddingTop: '110px' }}>
      <div
        ref={trackRef}
        onScroll={handleScroll}
        data-testid="mobile-carousel-track"
        className="carousel-track"
        role="group"
        aria-label="Collection carousel"
        style={{ display: 'flex', gap: `${CARD_GAP}px`, overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', paddingLeft: `${TRACK_PAD}px`, paddingRight: `${TRACK_PAD}px`, paddingBottom: '20px' }}
      >
        {PRODUCTS.map(p => (
          <div key={p.id} style={{ scrollSnapAlign: 'start', flexShrink: 0, width: `${CARD_W}px` }}>
            <ProductCard product={p} />
          </div>
        ))}
      </div>

      {/* Nav + counter */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', padding: '16px 24px 0' }}>
        <button
          onClick={() => scrollTo(-1)}
          data-testid="carousel-prev-btn"
          aria-label="Previous fragrance"
          aria-controls="collection-carousel-track"
          style={{ background: 'none', border: '1px solid rgba(201,169,110,0.3)', borderRadius: '2px', padding: '10px 14px', cursor: 'pointer', color: 'var(--grey)' }}
        >
          <ChevronLeft size={16} strokeWidth={1.5} aria-hidden="true" />
        </button>
        <span
          data-testid="carousel-counter"
          aria-live="polite"
          style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '2.5px', color: 'var(--grey)' }}
        >
          {String(currentIdx + 1).padStart(2, '0')} / {String(PRODUCTS.length).padStart(2, '0')}
        </span>
        <button
          onClick={() => scrollTo(1)}
          data-testid="carousel-next-btn"
          aria-label="Next fragrance"
          aria-controls="collection-carousel-track"
          style={{ background: 'none', border: '1px solid rgba(201,169,110,0.3)', borderRadius: '2px', padding: '10px 14px', cursor: 'pointer', color: 'var(--grey)' }}
        >
          <ChevronRight size={16} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ margin: '14px 24px 32px', height: '1px', background: 'rgba(201,169,110,0.1)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', background: 'var(--gold)', transition: 'width 0.2s ease', width: `${progress * 100}%` }} />
      </div>
    </div>
  );
};

/* ── Desktop GSAP carousel ──────────────────────────────────────────────── */
const DesktopCarousel = () => {
  const sectionRef = useRef(null);
  const trackRef   = useRef(null);
  const titleRef   = useRef(null);

  useEffect(() => {
    registerScrollTrigger();

    // Everything lives inside one context so ctx.revert() can clean up all of
    // it. The title tween used to sit outside, which is why this component
    // previously resorted to killing every ScrollTrigger on the page.
    const ctx = gsap.context(() => {
      reveal(titleRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' } }
      );

      const track = trackRef.current;
      if (!track || prefersReducedMotion()) return;

      const getScrollAmt = () => Math.max(track.scrollWidth - window.innerWidth, 0);
      gsap.to(track, {
        x: () => -getScrollAmt(),
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: () => `+=${getScrollAmt()}`,
          pin: true,
          scrub: 1.2,
          invalidateOnRefresh: true,
          anticipatePin: 1,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Under reduced motion the pin is skipped, so the track must scroll normally.
  const reduced = prefersReducedMotion();

  return (
    <div ref={sectionRef} style={{ background: 'var(--black)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, padding: '56px 48px 0', pointerEvents: 'none' }}>
        <div ref={titleRef} style={{ opacity: 0 }}>
          <span className="section-eyebrow">02 — Collection</span>
          <h2 style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: 'clamp(26px, 4vw, 48px)', letterSpacing: '6px', color: 'var(--gold)', fontWeight: 400, marginTop: '12px', textTransform: 'uppercase' }}>
            The Collection
          </h2>
        </div>
      </div>
      <div style={{ position: 'absolute', bottom: '30px', right: '48px', zIndex: 10, display: 'flex', alignItems: 'center', gap: '10px', pointerEvents: 'none' }}>
        <span style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '10px', letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--gold-dark)' }}>
          Scroll to explore
        </span>
        <ChevronRight size={12} color="var(--gold-dark)" aria-hidden="true" />
      </div>
      <div
        ref={trackRef}
        className="carousel-track"
        style={{ display: 'flex', alignItems: 'center', height: '100vh', paddingLeft: 'clamp(160px, 20vw, 340px)', paddingRight: '80px', gap: '20px', willChange: 'transform', overflowX: reduced ? 'auto' : 'visible' }}
      >
        {PRODUCTS.map(p => <ProductCard key={p.id} product={p} />)}
        <div style={{ flexShrink: 0, width: '60px' }} />
      </div>
    </div>
  );
};

/* ── Wrapper: CSS media-query driven, no JS state toggle ────────────────── */
const TheCollection = () => (
  <CollectionBoundary>
    <section id="collection" data-testid="collection-section" aria-label="The Collection" style={{ background: 'var(--black)' }}>

      {/* Desktop version — hidden on mobile via CSS */}
      <div className="collection-desktop">
        <DesktopCarousel />
      </div>

      {/* Mobile version — hidden on desktop via CSS */}
      <div className="collection-mobile">
        <div style={{ padding: '80px 24px 0' }}>
          <span className="section-eyebrow">02 — Collection</span>
          <h2 style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: '28px', letterSpacing: '5px', color: 'var(--gold)', fontWeight: 400, marginTop: '12px', textTransform: 'uppercase' }}>
            The Collection
          </h2>
        </div>
        <MobileCarousel />
      </div>

    </section>
  </CollectionBoundary>
);

export default TheCollection;
